import React, { CSSProperties, ReactNode } from "react";
import styled, { css } from "styled-components";

interface CardProps {
  height: string;
  checked: boolean;
  $variant: "default" | "arc";
  $cardWidth: string;
}

const Card = styled.div.attrs({
  className: "u-row",
})<CardProps>`
  position: relative;
  width: 100%;
  height: ${({ height }) => height};
  padding: 16px 12px;
  align-items: center;

  border-radius: 6px;
  border: ${({ checked }) =>
    checked
      ? "1px solid var(--soremo-blue)"
      : "1px solid var(--soremo-border)"};
  outline: ${({ checked }) =>
    checked
      ? "1px solid var(--soremo-blue)"
      : "1px solid var(--soremo-border)"};
  background-color: #fff;

  /* Card drop shadow */
  box-shadow: 2px 4px 8px 0px rgba(0, 0, 0, 0.05);

  @media (hover: hover) {
    &:hover {
      border: 1px solid
        ${({ checked }) =>
          checked ? "var(--soremo-blue)" : "var(--soremo-light-gray)"};
      outline: ${({ checked }) =>
        checked ? "1px solid var(--soremo-blue)" : "none"};
      box-shadow: 0px 0px 6px 0px
        ${({ checked }) =>
          checked ? "var(--soremo-blue)" : "var(--soremo-light-gray)"};
      cursor: pointer;
    }
  }

  /*
   * variant="arc" 用の上書き。
   * RadioCardGroup（円弧 → 水平のスクロール演出コンテナ）が
   * inline style で transform / opacity を毎フレーム書き込むため、
   * カード側ではベース位置（中央寄せ）だけ用意する。
   */
  ${({ $variant, $cardWidth }) =>
    $variant === "arc" &&
    css`
      position: absolute;
      top: 50%;
      left: 50%;
      width: ${$cardWidth};
      transform: translate(-50%, -50%);
      transform-origin: center center;
      will-change: transform, opacity;
    `}
`;

const StyledRadio = styled.div.attrs({
  className: "u-absolute material-symbols-rounded",
})<{ checked: boolean }>`
  top: 8px;
  right: 8px;
  /* カード全面に敷く背景サーフェス（絶対配置の子）より手前に出す */
  z-index: 1;
  color: ${({ checked }) =>
    checked ? "var(--soremo-blue)" : "var(--soremo-border)"};
`;

const StyledLabel = styled.div.attrs({
  className: "u-col u-gap4 u-w100",
})<{ width: string }>`
  padding-left: ${({ width }) => width};
`;

const GuideImage = styled.div<{
  width: string;
  height: string;
  name?: string;
  id?: string | number;
}>`
  position: absolute;
  top: 0px;
  left: 0px;

  width: calc(${({ width }) => width} - 2px);
  height: calc(${({ height }) => height} - 2px);

  border-radius: 6px 0px 0px 6px;
  background-image: url(${({ name, id }) =>
    new URL(`../../assets/images/card_${name}_${id}.webp`, import.meta.url).href});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

/**
 * 動的 URL（S3 等）のガイド画像。
 * height="auto" のカードでも使えるよう top/bottom で全高に追従させる。
 * $square 指定時は width と同じ高さの正方形（上端固定）で表示する
 * （マイページ通知と同じ左端の正方形画像。カードが伸びても正方形を維持）。
 */
const UrlGuideImage = styled.div<{ width: string; $url: string; $square?: boolean }>`
  position: absolute;
  top: 0px;
  left: 0px;
  ${({ $square }) => ($square ? "aspect-ratio: 1 / 1;" : "bottom: 0px;")}

  width: calc(${({ width }) => width} - 2px);

  border-radius: 6px 0px 0px 6px;
  background-image: url(${({ $url }) => $url});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

export interface RadioCardProps {
  name?: string;
  id?: string | number;
  checked?: boolean;
  width?: string;
  height?: string;
  /**
   * variant="arc" のとき、円弧/水平に並べたときのカード全体幅。
   * 既存の `width` は画像列／ラベル左パディング用途のままで互換維持。
   */
  cardWidth?: string;
  variant?: "default" | "arc";
  showImage?: boolean;
  /**
   * 動的な画像 URL を左端のガイド画像として表示する。
   * アセット名ベースの `showImage`（`card_{name}_{id}.webp`）の代替で、
   * 指定時はこちらが優先される。画像列の幅は `width` に従う。
   */
  imageUrl?: string;
  /**
   * imageUrl ガイド画像の形状。
   * - "band"（既定）: カード全高に伸縮する縦帯
   * - "square": width と同じ高さの正方形（上端固定・カードが伸びても正方形を維持）
   */
  imageShape?: "band" | "square";
  showRadio?: boolean;
  children?: ReactNode;
  onClick?: () => void;
  className?: string;
  style?: CSSProperties;
}

const RadioCard: React.FC<RadioCardProps> = ({
  name,
  id,
  checked = false,
  width = "128px",
  height = "256px",
  cardWidth = "240px",
  variant = "default",
  showImage,
  imageUrl,
  imageShape = "band",
  showRadio = true,
  children,
  onClick,
  className,
  style,
}) => {
  return (
    <Card
      height={height}
      checked={checked}
      onClick={onClick}
      $variant={variant}
      $cardWidth={cardWidth}
      className={className}
      style={style}
    >
      {showRadio && checked ? (
        <StyledRadio checked>
          radio_button_checked
        </StyledRadio>
      ) : showRadio ? (
        <StyledRadio checked={false}>
          radio_button_unchecked
        </StyledRadio>
      ) : (
        <></>
      )}
      <div className="u-col u-w100">
        <StyledLabel width={width}>{children}</StyledLabel>
      </div>
      {imageUrl ? (
        <UrlGuideImage width={width} $url={imageUrl} $square={imageShape === "square"} />
      ) : (
        showImage && (
          <GuideImage width={width} height={height} name={name} id={id} />
        )
      )}
    </Card>
  );
};

export default RadioCard;
